import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { validatePhone, validateZip, formatPhoneInput, formatZipInput } from '../../utils/validation';
import './SocialAuthCompletion.css';

// Phone and ZIP boxes only ever hold a phone number / a ZIP.
const FIELD_FILTERS = {
  phoneNumber: formatPhoneInput,
  zipCode: formatZipInput
};

const SocialAuthCompletion = ({
  show,
  onSuccess,
  onHide,
  provider,
  token,
  providerProfile
}) => {
  const { socialAuth } = useAuth();

  const [formData, setFormData] = useState({
    firstName: providerProfile?.firstName || '',
    lastName: providerProfile?.lastName || '',
    phoneNumber: '',
    identity: '',
    zipCode: '',
    need1: '',
    need2: '',
    need3: '',
    acceptTerms: false,
    newsletterOptIn: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const filter = FIELD_FILTERS[name];
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : filter ? filter(value) : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.identity) newErrors.identity = 'Please select how you identify yourself';
    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) newErrors.phoneNumber = 'Invalid phone number';
    if (formData.zipCode && !validateZip(formData.zipCode)) newErrors.zipCode = 'Zip code must be 5 digits';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms of service';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSubmitError('');

    try {
      const result = await socialAuth(provider, token, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber || null,
        identity: formData.identity,
        zipCode: formData.zipCode || null,
        need1: formData.need1 || null,
        need2: formData.need2 || null,
        need3: formData.need3 || null,
        termsAccepted: formData.acceptTerms,
        newsletterOptIn: formData.newsletterOptIn
      });

      if (result.success) {
        onSuccess();
      } else {
        setSubmitError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setSubmitError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const providerLabel = provider === 'google' ? 'Google' : 'Facebook';

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="login-modal" unmountOnExit>
      <Modal.Header closeButton>
        <Modal.Title>Complete Your Profile</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {submitError && <Alert variant="danger">{submitError}</Alert>}

        <div className="social-completion-header">
          {providerProfile?.picture && (
            <img src={providerProfile.picture} alt="Profile" />
          )}
          <div>
            <p className="provider-name">
              {providerProfile?.firstName} {providerProfile?.lastName}
            </p>
            <p className="provider-email">
              {providerProfile?.email} — signed in via {providerLabel}
            </p>
          </div>
        </div>

        <p className="text-muted mb-4">
          Just a few more details to finish setting up your account.
        </p>

        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  isInvalid={!!errors.firstName}
                />
                <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  isInvalid={!!errors.lastName}
                />
                <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>How do you identify yourself? *</Form.Label>
            <Form.Select
              name="identity"
              value={formData.identity}
              onChange={handleChange}
              isInvalid={!!errors.identity}
            >
              <option value="">Select...</option>
              <option value="parent">Parent/Guardian</option>
              <option value="caregiver">Caregiver</option>
              <option value="sibling">Sibling</option>
              <option value="family">Other Family Member</option>
              <option value="professional">Professional/Service Provider</option>
              <option value="self">Self-Advocate</option>
              <option value="other">Other</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.identity}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone Number (optional)</Form.Label>
            <Form.Control
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              isInvalid={!!errors.phoneNumber}
              placeholder="(555) 123-4567"
              inputMode="tel"
              autoComplete="tel"
            />
            <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Zip Code (optional)</Form.Label>
            <Form.Control
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              isInvalid={!!errors.zipCode}
              placeholder="12345"
              inputMode="numeric"
              autoComplete="postal-code"
            />
            <Form.Control.Feedback type="invalid">{errors.zipCode}</Form.Control.Feedback>
          </Form.Group>

          <div className="mb-3">
            <Form.Label className="d-block fw-semibold mb-1">
              What are your needs? <span className="text-muted fw-normal">(optional)</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="need1"
              value={formData.need1}
              onChange={handleChange}
              placeholder="Need 1 (e.g. Finding therapy services)"
              className="mb-2"
              maxLength={500}
            />
            <Form.Control
              type="text"
              name="need2"
              value={formData.need2}
              onChange={handleChange}
              placeholder="Need 2 (e.g. Understanding IEP options)"
              className="mb-2"
              maxLength={500}
            />
            <Form.Control
              type="text"
              name="need3"
              value={formData.need3}
              onChange={handleChange}
              placeholder="Need 3 (e.g. Connecting with other parents)"
              maxLength={500}
            />
          </div>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleChange}
              isInvalid={!!errors.acceptTerms}
              label={
                <span>
                  I accept the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  {' '}*
                </span>
              }
              feedback={errors.acceptTerms}
              feedbackType="invalid"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="newsletterOptIn"
              checked={formData.newsletterOptIn}
              onChange={handleChange}
              label="Subscribe to our newsletter for updates and resources"
            />
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className="w-100 mt-2"
            size="lg"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default SocialAuthCompletion;
